import React, { useState, useEffect, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Backdrop,
  Button
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Business as FundIcon,
  TrendingUp as PerformanceIcon,
  Security as RiskIcon,
  Assessment as AnalysisIcon,
  Settings as SettingsIcon,
  Visibility as ViewIcon,
  BarChart as ChartIcon,
  Link as AliasIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useAuth } from '../../auth/useAuth';
import { useNavigate } from 'react-router-dom';

// Import data context
import { HedgeFundDataProvider, useHedgeFundData } from './contexts/HedgeFundDataContext';

// Import functional components
import FundManagement from './components/FundManagement/FundManagement';
import PerformanceManagement from './components/PerformanceManagement/PerformanceManagement';
import FundAliasManagement from './components/FundAliasManagement/FundAliasManagement';
import RiskMonitoring from './components/RiskMonitoring/RiskMonitoring';
import DataManagementTabs from './components/DataManagementTabs/DataManagementTabs';
import ReportsCharts from './components/ReportsCharts/ReportsCharts';
import AlertSystem from './components/AlertSystem/AlertSystem';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hedgefund-tabpanel-${index}`}
      aria-labelledby={`hedgefund-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `hedgefund-tab-${index}`,
    'aria-controls': `hedgefund-tabpanel-${index}`,
  };
}

// Internal component that uses the data context
const HedgeFundDatabaseContent: React.FC = () => {
  const { user, logout, getAccessToken } = useAuth();
  const { loading, refreshAll } = useHedgeFundData();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [alertSystemOpen, setAlertSystemOpen] = useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleBackToPortal = () => {
    navigate('/');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load database statistics on component mount
  useEffect(() => {
    loadDatabaseStats();
  }, []);

  const loadDatabaseStats = async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load database statistics');
      }
      
      const data = await response.json();
      console.log('Database stats loaded:', data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      console.error('Error loading database stats:', err);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a237e' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackToPortal}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Hedge Fund Database
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              onClick={() => setAlertSystemOpen(true)}
              title="Alerts & Notifications"
            >
              <NotificationsIcon />
            </IconButton>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.name || user?.username || 'User'}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {(user?.name || user?.username || 'U').charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => {
                handleClose();
                // Profile dialog would go here
              }}>
                <AccountCircleIcon sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Welcome Section */}
        <Paper sx={{ p: 3, mb: 2, background: 'linear-gradient(135deg, #1a237e 0%, #3f51b5 100%)', color: 'white' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Hedge Fund Database System
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Comprehensive hedge fund monitoring and risk management system
          </Typography>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Navigation Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            px: { xs: 1, sm: 2 }, 
            pt: 1,
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="hedge fund database tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: { xs: 48, sm: 56 },
                    textTransform: 'none',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    fontWeight: 500,
                    minWidth: { xs: 80, sm: 120 },
                    padding: { xs: '6px 8px', sm: '12px 16px' }
                  },
                  '& .MuiTab-iconWrapper': {
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }
                }}
              >
                <Tab
                  icon={<FundIcon />}
                  label="Funds"
                  {...a11yProps(0)}
                />
                <Tab
                  icon={<PerformanceIcon />}
                  label="Performance"
                  {...a11yProps(1)}
                />
                <Tab
                  icon={<RiskIcon />}
                  label="Risk"
                  {...a11yProps(2)}
                />
                <Tab
                  icon={<ViewIcon />}
                  label="Data Management"
                  {...a11yProps(3)}
                />
                <Tab
                  icon={<ChartIcon />}
                  label="Reports"
                  {...a11yProps(4)}
                />
                <Tab
                  icon={<AliasIcon />}
                  label="Aliases"
                  {...a11yProps(5)}
                />
              </Tabs>
            </Box>
            
            {/* Loading Indicator and Refresh Button */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              ml: { xs: 0, sm: 2 },
              mt: { xs: 1, md: 0 },
              width: { xs: '100%', md: 'auto' },
              justifyContent: { xs: 'center', md: 'flex-end' }
            }}>
              {/* Global Loading Indicator */}
              {(loading.initialLoad || loading.funds || loading.fundClasses || loading.perfQualities || 
                loading.perfTypes || loading.strategies || loading.styles || loading.performances) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" sx={{ 
                    display: { xs: 'none', sm: 'block' }, 
                    color: 'text.secondary',
                    fontSize: '0.75rem'
                  }}>
                    Loading...
                  </Typography>
                </Box>
              )}
              
              {/* Global Refresh Button */}
              <IconButton
                onClick={refreshAll}
                disabled={loading.initialLoad || loading.funds || loading.fundClasses || loading.perfQualities || 
                         loading.perfTypes || loading.strategies || loading.styles || loading.performances}
                title="Refresh All Data"
                size="small"
                sx={{ p: 1 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <FundManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <PerformanceManagement />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <RiskMonitoring />
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <DataManagementTabs />
        </TabPanel>
        
        <TabPanel value={tabValue} index={4}>
          <ReportsCharts />
        </TabPanel>
        
        <TabPanel value={tabValue} index={5}>
          <FundAliasManagement />
        </TabPanel>
      </Container>

      {/* Alert System */}
      <AlertSystem 
        open={alertSystemOpen} 
        onClose={() => setAlertSystemOpen(false)} 
      />
    </Box>
  );
};

// Main wrapper component with data provider and loading overlay
const HedgeFundDatabase: React.FC = () => {
  return (
    <HedgeFundDataProvider>
      <HedgeFundDatabaseWithLoading />
    </HedgeFundDataProvider>
  );
};

// Component that shows loading states without blocking navigation
const HedgeFundDatabaseWithLoading: React.FC = () => {
  const { loading } = useHedgeFundData();

  return (
    <HedgeFundDatabaseContent />
  );
};

export default HedgeFundDatabase;
